import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, XCircle, Eye, BarChart3, Shield, Globe,
  Star, MessageCircle, Bot, Sparkles, Compass, Cpu, Zap, Search,
  AlertTriangle, Trophy, TrendingUp, Share2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_ICONS = {
  chatgpt: MessageCircle,
  claude: Bot,
  gemini: Sparkles,
  perplexity: Compass,
  google_ai: Search,
  copilot: Cpu,
  grok: Zap,
};

const ENGINE_COLORS = {
  chatgpt: '#10A37F',
  claude: '#D97706',
  gemini: '#4285F4',
  perplexity: '#1B9AAA',
  google_ai: '#EA4335',
  copilot: '#0078D4',
  grok: '#3C3226',
};

export default function ReportCard() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/reports/${reportId}`);
        setReport(res.data);
        document.title = `AI Recommendation Report — GetChosenFirst`;
      } catch (e) {
        setError('Report not found');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  const handleCTA = async () => {
    try { await axios.post(`${API}/reports/${reportId}/click`); } catch (e) { /* */ }
    const params = new URLSearchParams();
    params.set('ref', reportId);
    if (report?.profession) params.set('profession', report.profession);
    navigate(`/onboarding?${params.toString()}`);
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: 'AI Recommendation Report Card', text: `Check out this AI recommendations report!`, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1D6B4F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6">
        <h2 className="font-display text-2xl text-[#3C3226] mb-2">Report not found</h2>
        <p className="text-[#5C5347] text-center mb-6">This report card may have expired or been removed.</p>
        <button onClick={() => navigate('/')} className="rounded-none bg-[#3C3226] text-[#F0EBE3] px-8 py-3 font-medium">
          Go to GetChosenFirst
        </button>
      </div>
    );
  }

  const score = report?.score || 0;
  const scoreColor = score >= 60 ? '#1D6B4F' : score >= 30 ? '#EF9F27' : '#E24B4A';
  const professionLabel = report?.profession || 'professional';
  const cityLabel = report?.city ? `${report.city}${report.state ? `, ${report.state}` : ''}` : 'your area';
  const engines = report?.engine_summary || [];
  const signalHealth = report?.signal_health || {};
  const topCompetitors = report?.top_competitors || [];
  const topActions = report?.top_actions || [];
  const breakdown = report?.score_breakdown || {};

  return (
    <div className="min-h-screen bg-[#F7F4EF] flex flex-col" data-testid="report-card-page">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-[#D4C9B8]">
        <div className="font-display text-lg font-semibold text-[#3C3226] tracking-tight">GetChosenFirst</div>
        <button
          onClick={handleShare}
          className="flex items-center gap-1 text-xs text-[#5C5347] hover:text-[#3C3226] transition-colors"
          data-testid="share-report-btn"
        >
          <Share2 size={14} /> Share
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center px-6 pb-12 max-w-lg mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full space-y-5 mt-5"
        >
          {/* Section 1: Score Overview */}
          <div className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] p-6 text-center" data-testid="report-score-section">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5347] mb-1">
              AI Recommendation Report Card
            </p>
            {report?.business_name && (
              <p className="text-sm font-medium text-[#3C3226] mb-1">{report.business_name}</p>
            )}
            <p className="text-xs text-[#5C5347] mb-6">
              {professionLabel} in {cityLabel}
            </p>

            {/* Score ring */}
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#D4C9B8" strokeWidth="8" />
                <circle
                  cx="60" cy="60" r="52" fill="none"
                  stroke={scoreColor} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${score * 3.27} 327`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-3xl font-semibold" style={{ color: scoreColor }} data-testid="report-score">
                  {score}
                </span>
                <span className="text-[10px] text-[#5C5347]">/100</span>
              </div>
            </div>

            <p className="text-xs text-[#5C5347]">
              Visible in {report?.total_mentions || 0} of {report?.total_queries || 0} AI queries
            </p>

            {/* Score breakdown bars */}
            {Object.keys(breakdown).length > 0 && (
              <div className="mt-5 space-y-2 text-left">
                {Object.entries(breakdown).map(([key, data]) => (
                  <div key={key}>
                    <div className="flex justify-between text-[10px] mb-0.5">
                      <span className="text-[#5C5347]">{data.label}</span>
                      <span className="font-medium text-[#3C3226]">{data.score}/100</span>
                    </div>
                    <div className="h-1 bg-[#F7F4EF] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-none transition-all duration-700"
                        style={{ width: `${data.score}%`, backgroundColor: data.score >= 60 ? '#1D6B4F' : data.score >= 35 ? '#EF9F27' : '#E24B4A' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 2: AI Engine Presence */}
          {engines.length > 0 && (
            <div className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] p-5" data-testid="report-engine-section">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={15} className="text-[#1D6B4F]" />
                <h3 className="text-sm font-medium text-[#3C3226]">AI Engine Results</h3>
              </div>
              <div className="space-y-2">
                {engines.map((eng) => {
                  const Icon = ENGINE_ICONS[eng.engine] || Cpu;
                  const color = ENGINE_COLORS[eng.engine] || '#888';
                  const isMentioned = eng.status === 'mentioned';
                  return (
                    <div key={eng.engine} className="flex items-center gap-3 py-1.5" data-testid={`report-engine-${eng.engine}`}>
                      <Icon size={14} style={{ color }} className="flex-shrink-0" />
                      <span className="text-xs font-medium text-[#3C3226] flex-1">{eng.label}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-[#5C5347]">{eng.mentions}/{eng.total}</span>
                        <div className="w-12 h-1.5 bg-[#F7F4EF] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${eng.pct}%`, backgroundColor: isMentioned ? color : '#E24B4A' }} />
                        </div>
                        {isMentioned ? (
                          <CheckCircle2 size={12} className="text-[#1D6B4F]" />
                        ) : (
                          <XCircle size={12} className="text-[#E24B4A]" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section 3: How AI Decides */}
          {signalHealth.overall_score != null && (
            <div className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] p-5" data-testid="report-signal-section">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={15} className="text-[#1D6B4F]" />
                <h3 className="text-sm font-medium text-[#3C3226]">How AI Decides</h3>
                <span className="text-xs font-semibold text-[#3C3226] ml-auto">{signalHealth.overall_score}/100</span>
              </div>
              <div className="h-1.5 bg-[#F7F4EF] rounded-full overflow-hidden mb-3">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${signalHealth.overall_score}%`, backgroundColor: signalHealth.overall_score >= 60 ? '#1D6B4F' : signalHealth.overall_score >= 35 ? '#EF9F27' : '#E24B4A' }}
                />
              </div>
              <div className="flex gap-4">
                <span className="text-[10px] text-[#1D6B4F] font-medium">{signalHealth.strong_count} strong</span>
                <span className="text-[10px] text-[#EF9F27] font-medium">{signalHealth.needs_work_count} needs work</span>
                <span className="text-[10px] text-[#E24B4A] font-medium">{signalHealth.missing_count} missing</span>
              </div>
            </div>
          )}

          {/* Section 4: Top Competitors */}
          {topCompetitors.length > 0 && (
            <div className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] p-5" data-testid="report-competitors-section">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={15} className="text-[#EF9F27]" />
                <h3 className="text-sm font-medium text-[#3C3226]">Top Competitors</h3>
              </div>
              <div className="space-y-2">
                {topCompetitors.map((comp, i) => (
                  <div key={comp.name || `comp-${i}`} className="flex items-center gap-3 py-1" data-testid={`report-competitor-${i}`}>
                    <span className="text-xs font-bold text-[#5C5347] w-5 text-center">#{i + 1}</span>
                    <span className="text-xs text-[#3C3226] flex-1">{comp.name}</span>
                    <span className="text-[10px] text-[#5C5347]">{comp.mentions} AI mentions</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 5: Top 3 Actions */}
          {topActions.length > 0 && (
            <div className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] p-5" data-testid="report-actions-section">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={15} className="text-[#C67A5C]" />
                <h3 className="text-sm font-medium text-[#3C3226]">Top 3 Actions</h3>
              </div>
              <div className="space-y-2.5">
                {topActions.map((action, i) => (
                  <div key={`action-${i}`} className="flex items-start gap-2.5" data-testid={`report-action-${i}`}>
                    <span className="w-5 h-5 rounded-none bg-[#C67A5C]/10 text-[#C67A5C] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    <div>
                      <p className="text-xs font-medium text-[#3C3226]">{action.description || action.signal}</p>
                      {action.impact && (
                        <span className={`text-[9px] font-medium ${action.impact === 'high' ? 'text-[#E24B4A]' : action.impact === 'medium' ? 'text-[#EF9F27]' : 'text-[#1D6B4F]'}`}>
                          {action.impact} impact
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] p-6 text-center">
            <p className="text-sm text-[#3C3226] font-medium mb-1">
              How does <em>your</em> business compare?
            </p>
            <p className="text-xs text-[#5C5347] mb-4">Get your free AI recommendations scan in 60 seconds</p>
            <button
              onClick={handleCTA}
              className="w-full rounded-none bg-[#2C2520] hover:bg-[#3C3226] text-[#F0EBE3] py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
              data-testid="report-cta-btn"
            >
              See your score — free <ArrowRight size={16} />
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-[10px] text-[#BBB]">
            Report generated {new Date(report?.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </motion.div>
      </main>
    </div>
  );
}
